import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { switchOrg } from "../store/actions/auth";
import { selectOrgSwitcherOrganizations } from "../store/selectors/orgSwitcher";
import type { AppDispatch } from "../store/state/rootStore";
import type { RootState } from "../store/state/rootReducer";

/**
 * Legacy org-scoped links (e.g. /organization/:organization_code/projects/:id) are shared
 * in emails/bookmarks. When a multi-org user opens one for an org that isn't their active
 * org, the page content follows the URL but the active org (sidebar switcher) stayed stale.
 *
 * This treats the URL org code as an org switch: if it maps to one of the user's orgs and
 * differs from the active one, switch and reload the same page WITHOUT the
 * `/organization/:code` prefix — so the (now-switched) active org is the single source of
 * truth and future org flips aren't intercepted by the URL. Returns `isSwitchingOrg` so the
 * page can show a loader instead of painting under the old org before the reload lands.
 *
 * The switch decision is made exactly ONCE per mount (as soon as both the URL org and the
 * active org are known). Not reacting to later `activeOrgId` changes is deliberate: it stops
 * the hook from fighting a user's own sidebar switch while still on an org-scoped URL.
 */
export const useSyncActiveOrgFromUrl = (): boolean => {
  const dispatch = useDispatch<AppDispatch>();
  const { organization_code: urlOrgCode } = useParams();
  const orgs = useSelector(selectOrgSwitcherOrganizations);
  const activeOrgId = useSelector((state: RootState) => (state.auth.organization_info as { _id?: string } | null)?._id);

  const decisionRef = useRef<null | { targetId: string | null }>(null);
  if (decisionRef.current === null && activeOrgId) {
    let targetId: string | null = null;
    if (urlOrgCode) {
      const target = orgs.find((org) => org.organizational_code === urlOrgCode);
      if (target && String(target._id) !== String(activeOrgId)) {
        targetId = String(target._id);
      }
    }
    decisionRef.current = { targetId };
  }

  // Only cleared on a failed switch (no reload) so the loader never sticks.
  const [switchFailed, setSwitchFailed] = useState(false);
  const isSwitchingOrg = decisionRef.current?.targetId != null && !switchFailed;

  const decided = decisionRef.current !== null;
  useEffect(() => {
    const targetId = decisionRef.current?.targetId;
    if (!targetId) return;
    // Drop the /organization/:code prefix; fall back to /projects if nothing is left.
    const strippedPath = window.location.pathname.replace(/^\/organization\/[^/]+/, "") || "/projects";
    void dispatch(switchOrg(targetId, strippedPath + window.location.search)).then((ok) => {
      if (!ok) setSwitchFailed(true);
    });
  }, [decided, dispatch]);

  return isSwitchingOrg;
};
